import { useToast } from 'primevue/usetoast'
import { shallowRef, toValue } from 'vue'
import { useI18n } from 'vue-i18n'

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
]

/**
 * Uploads planning drawings through the existing photo pipeline
 * (photo.upload → PhotoService: HEIC/WEBP re-encoded to JPEG, which is also
 * what the Word export requires) and returns the created {id, url} rows for
 * insertion as planningImage nodes.
 *
 * Upload-first, insert-on-success: nothing enters the editor (or its
 * undo/redo history) until the photo row exists.
 */
export function usePlanningImageUpload(objectId) {
  const { t } = useI18n()
  const toast = useToast()

  const uploading = shallowRef(false)
  const progress = shallowRef(0)

  /**
   * @param {File[]} files
   * @returns {Promise<Array<{id: number, url: string}>>}
   */
  const uploadImages = async files => {
    const images = [...files].filter(file => ACCEPTED_TYPES.includes(file.type))
    if (!images.length) {
      toast.add({
        severity: 'warn',
        summary: t('messages.editor.unsupportedFileType'),
        life: 4000,
      })
      return []
    }

    uploading.value = true
    progress.value = 0

    const formData = new FormData()
    formData.append('objectId', toValue(objectId))
    formData.append('photoType', 'planning')
    images.forEach(file => formData.append('files[]', file))

    try {
      const { data } = await axios.post(route('photo.upload'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: event => {
          if (event.total) {
            progress.value = Math.round((event.loaded * 100) / event.total)
          }
        },
      })

      return data.photos ?? []
    } catch (error) {
      // 422 messages (decode failures, mime rejections) are shown verbatim —
      // PhotoService raises them user-readable in Estonian.
      const message =
        error.response?.status === 422
          ? Object.values(error.response.data?.errors ?? {}).flat()[0]
          : null
      toast.add({
        severity: 'error',
        summary: message ?? t('messages.editor.uploadFailed'),
        life: 5000,
      })
      return []
    } finally {
      uploading.value = false
    }
  }

  return { uploading, progress, uploadImages }
}
